import { useEffect, useRef } from "react";

/**
 * پشته‌ی لایه‌ها، روی تاریخچه‌ی مرورگر.
 *
 * چرا لازم شد: روی گوشی «بازگشت» — چه دکمه‌ی اندروید چه اشاره‌ی لبه — طبیعی‌ترین
 * راهِ بستنِ هر چیزی است که باز شده. ولی هیچ لایه‌ای از این بازی ورودیِ تاریخچه
 * نمی‌گذاشت، پس «بازگشت» روی پنجره‌ی انتخابِ چهره یا وسطِ دست، آدم را از **کلِ
 * صفحه** بیرون می‌انداخت. برگشتن هم یعنی بارِ دوباره‌ی برنامه و از دست رفتنِ میز.
 *
 * حالا هر لایه هنگامِ باز شدن یک ورودی می‌گذارد و «بازگشت» همان بالاترین لایه را
 * می‌بندد. لایه‌ای که نباید با بازگشت برود (پنجره‌ی خواندنِ حکم، میزِ در جریان)
 * از دستگیره‌اش `true` برمی‌گرداند: ورودی دوباره گذاشته می‌شود و صفحه سرِ جایش
 * می‌مانَد.
 *
 * منطق جدا از `window` نوشته شده تا بشود بی‌مرورگر آزمودش؛ نمونه‌ی زنده پایینِ
 * همین فایل به `history` وصل می‌شود.
 */

/** `true` یعنی «نرفتم» — لایه باز می‌مانَد و ورودیِ تاریخچه دوباره گذاشته می‌شود. */
export type BackHandler = () => boolean | void;

export interface HistoryLike {
  pushState(state: unknown, title: string): void;
  back(): void;
}

type Entry = { id: number; onBack: BackHandler };

export class BackStack {
  private entries: Entry[] = [];
  private nextId = 1;
  /**
   * شمارِ popstateهایی که خودمان راه انداخته‌ایم.
   *
   * بستنِ لایه از داخلِ برنامه (دکمه‌ی «بستن») هم باید ورودیِ تاریخچه را بخورد،
   * وگرنه «بازگشت» بعدی یک ورودیِ مرده را می‌بلعد و انگار کار نمی‌کند. ولی
   * `back()` خودش popstate می‌زند؛ آن یکی را باید نادیده گرفت، نه این‌که لایه‌ی
   * بعدی را هم ببندد.
   */
  private pending = 0;

  constructor(private readonly hist: HistoryLike) {}

  get depth(): number { return this.entries.length; }

  /** لایه‌ای باز شد. تابعِ برگشتی آن را می‌بندد (و ورودی‌اش را پس می‌گیرد). */
  push(onBack: BackHandler): () => void {
    const id = this.nextId++;
    this.entries.push({ id, onBack });
    this.hist.pushState({ bazivan: id }, "");
    return () => this.close(id);
  }

  /** رویدادِ popstate. */
  pop(): void {
    if (this.pending > 0) { this.pending--; return; }
    const top = this.entries.pop();
    if (!top) return;
    if (top.onBack() === true) {
      this.entries.push(top);
      this.hist.pushState({ bazivan: top.id }, "");
    }
  }

  /**
   * بستن از داخلِ برنامه.
   *
   * اگر «بازگشت» خودش این لایه را بسته باشد، دیگر در پشته نیست و این‌جا کاری
   * نمی‌شود — وگرنه بستنِ ری‌اکتی یک ورودیِ اضافه می‌خورد و بازگشت یک پله زیادی
   * می‌رفت.
   *
   * بسته‌شدن‌ها معمولاً از بالا به پایین‌اند؛ اگر لایه‌ای از وسط بسته شود باز هم
   * شمارِ ورودی‌ها با شمارِ لایه‌ها می‌خوانَد، چون هر بستن دقیقاً یک ورودی
   * برمی‌دارد و همه‌ی ورودی‌ها یک نشانی دارند.
   */
  private close(id: number): void {
    const i = this.entries.findIndex((e) => e.id === id);
    if (i < 0) return;
    this.entries.splice(i, 1);
    this.pending++;
    this.hist.back();
  }
}

const noHistory: HistoryLike = { pushState() {}, back() {} };

export const backStack = new BackStack(
  typeof window === "undefined" ? noHistory : window.history,
);

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => backStack.pop());
}

/**
 * تا وقتی `active` است، «بازگشت» به `onBack` می‌رسد و نه به بیرونِ صفحه.
 *
 * دستگیره در رف نگه داشته می‌شود تا تغییرِ تابع در هر رندر، ورودیِ تاریخچه را
 * دوباره نگذارد.
 */
export function useBackScreen(active: boolean, onBack: BackHandler): void {
  const ref = useRef(onBack);
  ref.current = onBack;
  useEffect(() => {
    if (!active) return;
    return backStack.push(() => ref.current());
  }, [active]);
}
